import React, { PropTypes } from "react";
import ReactDOM from "react-dom";
import { Table } from "fixed-data-table";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import ReactTimeout from "react-timeout";
import { connect } from "react-redux";
import { reset } from "redux-form";
import { compose } from "redux";

import { RectangleSpinner } from "../spinners";
import { TablePages, TableQ } from "../DataTable";
import { getLocalStorageFilter, setLocalStorageFilters } from "../LocalStorageFilters/LocalStorageFilters";
import { resetURLQueryParams, updateCurrentURLV2 } from "../../../../utils/url";
import { createColumnV2 } from "./";
import { LIKE, EQUALS } from "../GraphQL/GraphQLFilterTypes";
import { cleanStringForFTS } from "../GraphQL/GraphQLQueryHelper";
import RefreshButton from "../RefreshButton";

const MINIMUM_QUERY_LENGTH = 3;

class DataTableV2 extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            tableWidth: 400,
            ready: false,
            q: "",
            sortUrlKey: "sort",
            activeSearchedCols: [],
        };
        this.setQ = this.setQ.bind(this);
        this.sendQ = _.debounce(this.sendQ.bind(this), 250);
        this.setOffset = this.setOffset.bind(this);
        this.setSort = this.setSort.bind(this);
        this.throttle = this.throttle.bind(this);
        this._update = this._update.bind(this);
        this.setFilters = this.setFilters.bind(this);
        this.resetTableQForm = this.resetTableQForm.bind(this);
        this.clearDataTableFilters = this.clearDataTableFilters.bind(this);
        this.asyncColSearch = _.debounce(this.asyncColSearch.bind(this), 250);
        this.setActiveCol = this.setActiveCol.bind(this);
        this.getRefreshButton = this.getRefreshButton.bind(this);
        this.reloadData = this.reloadData.bind(this);
    }

    componentDidMount() {
        const { getData, filters, sortBy } = this.props;
        const { rowsPerPage } = this.props.pagination;

        let win = window;
        if (win.addEventListener) {
            win.addEventListener("resize", this.throttle, false);
            win.addEventListener("scroll", this.throttle, false);
        } else if (win.attachEvent) {
            win.attachEvent("onresize", this.throttle);
            win.attachEvent("onscroll", this.throttle);
        } else {
            win.onresize = this._update.bind(this);
            win.onscroll = this._update.bind(this);
        }
        this._update(this);
        this.setState({ ready: true }, () => getData(0, rowsPerPage, this.state.q, sortBy, filters, true));
    }

    componentWillReceiveProps(nextProps) {
        this._update(this, nextProps);
    }

    componentWillUpdate() {
        this.props.setTimeout(() => {
            const windowWidth = window.innerWidth > 1024 ? window.innerWidth : 1024;
            const titleWidth = document.getElementsByClassName("datatable-actions-section")[0]
                ? windowWidth - document.getElementsByClassName("datatable-actions-section")[0].offsetWidth - 80 + "px"
                : "";
            if (titleWidth !== this.state.titleWidth) {
                this.setState({ titleWidth });
            }
        }, 0);
    }

    componentWillUnmount() {
        let win = window;
        if (win.removeEventListener) {
            win.removeEventListener("resize", this.throttle, false);
            win.removeEventListener("scroll", this.throttle, false);
        } else if (win.detachEvent) {
            win.detachEvent("onresize", this.throttle);
            win.detachEvent("onscroll", this.throttle);
        } else {
            win.onresize = null;
            win.onscroll = null;
        }
    }

    setActiveCol(col, remove, reset = false) {
        /**
         * activeSearchedCols state will allow us to keep track of all the cols that have an active async search.
         * By doing that, we can know which filter dropdowns to update.
         * If remove param is true, we need to remove the emptied col from the state.
         *
         * The reset variable is used in a edge case where if the user presses "Empty all filters", while some filters are loaded,
         * the activeSearchedCols state is never cleared, which can cause dropdowns to auto update."
         */
        let cols = [];

        if (reset) {
            this.setState({ activeSearchedCols: [] });
        } else {
            if (!remove) {
                cols = [...this.state.activeSearchedCols, col];
                let colsSet = [...new Set(cols)]; // Allows us to remove duplicates.
                this.setState({ activeSearchedCols: colsSet });
            } else {
                cols = this.state.activeSearchedCols.filter((stateCol) => stateCol != col);
                this.setState({ activeSearchedCols: cols });
            }
        }
    }

    throttle() {
        _.throttle(this._update.bind(this, this), 0)();
    }

    _update(node) {
        node = ReactDOM.findDOMNode(node);

        this.setState({
            tableWidth: node.clientWidth,
        });
    }

    sendQ(q) {
        const { getData, filters, sortBy, cleanQ } = this.props;
        const { rowsPerPage } = this.props.pagination;

        if (typeof q !== "string") {
            return;
        }

        if (q.length == 0 || q.length >= MINIMUM_QUERY_LENGTH) {
            if (cleanQ) {
                q = cleanStringForFTS(q);
            }
            const promise = getData(0, rowsPerPage, q, sortBy, filters);

            if (promise)
                promise.then(() => {
                    this.setState({ fetching: false });
                });
            else this.setState({ fetching: false });
        }
    }

    setQ(q) {
        this.setState({ q, fetching: true });
        this.sendQ(q);
    }

    setOffset(offset) {
        const { getData, filters, sortBy, deactivateCheckboxes } = this.props;
        const { rowsPerPage } = this.props.pagination;
        deactivateCheckboxes();

        getData(offset, rowsPerPage, this.state.q, sortBy, filters, true, this.state.activeSearchedCols);
    }

    setSort(sortBy, onColChange, headerSelections) {
        const { getData, dataTableName, pagination, filters, deactivateCheckboxes } = this.props;
        deactivateCheckboxes();
        onColChange(sortBy, headerSelections.key, undefined, true); // This is used by the data table parent component to update states.
        /**
         * Parent must be able to tell the difference between sort change and filter change (sort always has : limiter.)
         */
        const sortVal = sortBy[0];
        setLocalStorageFilters(dataTableName + ".sortBy", sortVal);
        updateCurrentURLV2(this.state.sortUrlKey, sortVal);
        getData(
            pagination.offset,
            pagination.rowsPerPage,
            this.state.q,
            sortBy,
            filters,
            true,
            this.state.activeSearchedCols
        );
    }

    clearDataTableFilters() {
        const { dataTableName, resetFiltersState, deactivateCheckboxes, refetchFilters } = this.props;
        if (deactivateCheckboxes) {
            deactivateCheckboxes();
        }
        this.resetTableQForm();
        if (refetchFilters) {
            refetchFilters();
        }
        Object.keys(localStorage).forEach((key) => {
            /**
             * Search for a particular table's filter,
             * then clear all of them
             */
            if (key.startsWith(dataTableName)) {
                localStorage.removeItem(key);
            }
        });

        if (resetFiltersState) {
            /**
             * If you have some logic to execute in the parent dt component, this prop function can be
             * called.
             */
            resetFiltersState(this.setActiveCol);
            /** setActiveCol is a function that can be called from the parent container (passed as parameter). This allows us the parent component to
             * control the filter reset button, and to empty active cols to avoid unwanted behaviour.
             */
        }

        resetURLQueryParams();
    }

    santizeSelectedFilters(values) {
        let valsCopy = JSON.parse(
            JSON.stringify(values)
        ); /** Deep copy is needed to not modify the array while looping over it. */
        values.forEach((val, i) => {
            for (const [key, value] of Object.entries(val)) {
                if (value.includes(";")) {
                    valsCopy[i][key] = value.replace(/;/g, "_sc_");
                }
            }
        });

        return valsCopy;
    }

    setFilters(onColChange, values, headerSelections, emptyList, isFrontFilter) {
        const {
            getData,
            dataTableName,
            generateFilters,
            filters,
            sortBy,
            pagination,
            deactivateCheckboxes,
        } = this.props;
        deactivateCheckboxes();
        const key = headerSelections["key"];
        values = this.santizeSelectedFilters(values, key);
        const currentFilters = [...filters];
        const updatedFilters = currentFilters.filter((filter) => !filter.includes(headerSelections.col)); // Removing all filters related to the column, so we can get an up to date filter state.

        const generatedFilters = generateFilters(
            values,
            headerSelections.key,
            headerSelections.modelName,
            headerSelections.urlKey
        );
        const filtersToApply = onColChange(values, headerSelections.key, generatedFilters); // This is used by the data table parent component to update states.
        let combinedFilters = new Set([...updatedFilters, ...filtersToApply]);
        combinedFilters = Array.from(combinedFilters);
        setLocalStorageFilters(
            dataTableName + "." + headerSelections.urlKey,
            filtersToApply.length > 0 ? filtersToApply.join(";") : ""
        );
        const urlValues = values.length > 0 ? values.map((value) => value[headerSelections.key]) : null;
        updateCurrentURLV2(headerSelections.urlKey, urlValues);
        getData(
            0,
            pagination.rowsPerPage,
            this.state.q,
            sortBy,
            combinedFilters,
            true,
            this.state.activeSearchedCols,
            headerSelections.col,
            isFrontFilter,
            emptyList
        );
    }

    resetTableQForm() {
        this.setQ("");
        this.props.resetTableQForm();
    }

    reloadData() {
        this.setState({ fetching: true });
        this.props.deactivateCheckboxes();
        const { refreshFunction, filters, rowsPerPage } = this.props;
        refreshFunction(0, rowsPerPage, this.state.q, this.props.sortBy, filters).then(() => {this.setState({ fetching: false });});
    }

    getRefreshButton(disable) {
        const { fetching } = this.state;
        return (
        <div className="datatable-header">
            <div className="datatable-button-container">
            <RefreshButton
                onClick={this.reloadData}
                disabled={fetching || disable || this.props.fetchingCheckboxFilter}
                label={<FormattedMessage id="datatable.refreshData"/>}
            />
            </div>
        </div>
        );
    }

    asyncColSearch(asyncValue, prevSearch, setCurrentFilter, headerSelections, isFrontFilter = false) {
        const {
            pagination,
            sortBy,
            filters,
            getData,
            dataTableName,
            setAsyncFilters,
            isGraphQL,
            deactivateCheckboxes,
        } = this.props;
        deactivateCheckboxes();
        let filterArr = [...filters];
        let localStorageValue = "";

        if (prevSearch) {
            filterArr = filterArr.filter((filter) => filter !== prevSearch).filter((filter) => filter);
        }

        setCurrentFilter(asyncValue);
        /**
         * Function passed from SelectHeaderV2.
         */

        const splitPattern = isGraphQL ? LIKE : EQUALS;

        if (isGraphQL) {
            if (asyncValue.split(splitPattern)[1].length < MINIMUM_QUERY_LENGTH) {
                // 3 = The minimum characters for an API search.
                asyncValue = "";
            }
        }

        let asyncFilters = [...filterArr, asyncValue && asyncValue];
        const localStorageKey = dataTableName + "." + headerSelections.col;
        const currentLocalStorage = getLocalStorageFilter(localStorageKey);

        let currentLocalStorageFilters = currentLocalStorage ? currentLocalStorage.split(";") : [];
        let localStorageArr = currentLocalStorageFilters.filter((filter) => filter !== prevSearch && filter); // Always removing previous search on same col.

        localStorageValue = localStorageArr.length > 0 ? localStorageArr.join(";") + ";" + asyncValue : asyncValue;

        asyncFilters = asyncFilters.filter((f) => f);

        setAsyncFilters(headerSelections.col, asyncValue);

        if (localStorageValue === ";") {
            localStorageValue = "";
        }

        setLocalStorageFilters(localStorageKey, localStorageValue);

        let urlValues = localStorageValue.split(";").filter((val) => val);
        let urlArr = urlValues.map((val) => {
            if (val) {
                const likeOperator = val.split(LIKE)[1];
                if (likeOperator) {
                    return likeOperator;
                } else {
                    return val.split(EQUALS)[1];
                }
            }
        });

        updateCurrentURLV2(headerSelections.col, urlArr, true, asyncValue);

        getData(
            0, // 0 Offset for every search.
            pagination.rowsPerPage,
            this.state.q,
            sortBy,
            asyncFilters,
            true,
            this.state.activeSearchedCols,
            headerSelections.key,
            isFrontFilter,
            asyncValue === ""
        );
        /** Parameters : (offset, limit, q, sortBy, filters, forceGetData, createFilterLists - optional, searchColKey - optional, isFrontFilter - false by default) **/
    }

    render() {
        const {
            customDataTableOptions,
            cols,
            data: rawData,
            dataFilter,
            filterDataTableOptions,
            pagination,
            rowHeight,
            showFilters,
            showSelectedCheckboxes,
            showOverflow,
            showTitle,
            sortBy,
            title,
            totalRows,
            customItemOptions,
            isGraphQL,
            showEmptyFiltersBtn,
            customEmptyBtnFilterClass,
            hideQ,
            fetchingCheckboxFilter,
            refreshFunction
        } = this.props;

        const { fetching, tableWidth, q } = this.state;

        if (!this.state.ready) {
            return <div />;
        }
        const headerHeight = 42;
        const dataTableClassNames = classNames({
            datatable: true,
            "show-overflow-v2": showOverflow,
        });
        const data = dataFilter ? dataFilter(rawData) : rawData;
        const dataLength = data ? data.length : 0;

        let colsToDisplay = [...cols]; // If we modify the cols prop directly, it will cause a rendering bug, so we copy the array instead.

        if (customItemOptions) {
            /**
             * This is the actions buttons section. We must pass a component (OptionsMenu) that will be rendered as the
             * last col of the data table.
             */
            colsToDisplay.push({
                name: (
                    <span className="Select-placeholder">
                        <FormattedMessage id="datatable.actions" />
                    </span>
                ),
                display: (followUp) => customItemOptions.generateComponent(followUp),
                widthPct: customItemOptions.widthPct,
            });
        }

        let columns = colsToDisplay.map((col) => {
            return createColumnV2(
                data,
                col,
                sortBy,
                this.setSort,
                this.setFilters,
                tableWidth,
                this.asyncColSearch,
                isGraphQL,
                MINIMUM_QUERY_LENGTH,
                this.setActiveCol
            );
        });

        let mapping = {};

        mapping.showSelectedCheckboxes = showSelectedCheckboxes;
        const notLoaded = data == null && !totalRows;

        return (
            <div className={dataTableClassNames}>
                {showTitle && (
                    <div className="datatable-header">
                        <div className="datatable-title-container">
                            <h2 className="datatable-title">{title}</h2>
                        </div>
                    </div>
                )}
                {refreshFunction != undefined && this.getRefreshButton(notLoaded)}
                {showFilters && (
                    <div className="datatable-actions-section">
                        {filterDataTableOptions}
                        {(q !== "" || totalRows || fetching || fetchingCheckboxFilter) && !hideQ ? <TableQ setQ={this.setQ} q={q} /> : null}
                        <div className="dataTable-add-button">
                            {customDataTableOptions}
                            {showEmptyFiltersBtn && (
                                <button
                                    className={`btn btn-primary empty-filter-btn ${customEmptyBtnFilterClass}`}
                                    type="button"
                                    disabled={notLoaded || fetching || fetchingCheckboxFilter}
                                    onClick={this.clearDataTableFilters}
                                >
                                    <FormattedMessage id="dtv2ClearFilter" />
                                </button>
                            )}
                        </div>
                    </div>
                )}
                {showSelectedCheckboxes != 0 && (
                    <FormattedMessage id="common.showSelectedCheckboxes" values={mapping}>
                        {(m) => <div className="formatted-confirmation" dangerouslySetInnerHTML={{ __html: m }} />}
                    </FormattedMessage>
                )}
                <div>
                    <Table
                        rowsCount={dataLength}
                        rowHeight={rowHeight}
                        headerHeight={headerHeight}
                        width={tableWidth}
                        height={dataLength * rowHeight + headerHeight + dataLength}
                    >
                        {columns}
                    </Table>

                    {data && data.length === 0 && (
                        <div className="empty-datatable">
                            <FormattedMessage id="datatable.noData" />
                        </div>
                    )}
                    {data == null && <RectangleSpinner />}
                </div>

                <TablePages
                    totalRows={totalRows}
                    rowsPerPage={pagination.rowsPerPage}
                    offset={pagination.offset}
                    setOffset={this.setOffset}
                />
            </div>
        );
    }
}

DataTableV2.propTypes = {
    customDataTableOptions: PropTypes.node,
    cols: PropTypes.array.isRequired,
    containerWidth: PropTypes.number,
    data: PropTypes.array,
    dataFilter: PropTypes.func,
    getData: PropTypes.func.isRequired,
    filters: PropTypes.array,
    offset: PropTypes.number.isRequired,
    setTimeout: PropTypes.func,
    showFilters: PropTypes.bool,
    showSelectedCheckboxes: PropTypes.number.isRequired,
    showOverflow: PropTypes.bool,
    showTitle: PropTypes.bool,
    sortBy: PropTypes.array,
    title: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    totalRows: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number,
    rowHeight: PropTypes.number,
    filterDataTableOptions: PropTypes.object,
    customItemOptions: PropTypes.object,
    pagination: PropTypes.object,
    dataTableName: PropTypes.string,
    isGraphQL: PropTypes.bool,
    showEmptyFiltersBtn: PropTypes.bool,
    resetFiltersState: PropTypes.func,
    customEmptyBtnFilterClass: PropTypes.string,
    refreshFunction: PropTypes.func,
    generateFilters: PropTypes.func,
    setAsyncFilters: PropTypes.func,
    deactivateCheckboxes: PropTypes.func,
    resetTableQForm: PropTypes.func,
    hideQ: PropTypes.bool,
    cleanQ: PropTypes.bool,
    refetchFilters: PropTypes.func,
    fetchingCheckboxFilter: PropTypes.bool,
};

const mapDispatchToProps = (dispatch) => ({
    resetTableQForm: () => {
        dispatch(reset("tableQ"));
    },
});

DataTableV2.defaultProps = {
    showFilters: true,
    showSelectedCheckboxes: 0,
    showOverflow: true,
    showTitle: false,
    rowHeight: 42,
    isGraphQL: false,
    hideQ: false,
    cleanQ: false,
    fetchingCheckboxFilter: false
};

export default compose(connect(null, mapDispatchToProps), ReactTimeout)(DataTableV2);



// WEBPACK FOOTER //
// ./src/components/common/DataTableV2/DataTableV2.js